import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  Timestamp,
  startAfter,
} from 'firebase/firestore'
import { db } from '@/lib/firebase/config'
import type { AuditLog, UserRole } from '@/types'

// ============================================
// TYPES
// ============================================

interface CreateAuditLogParams {
  agencyId: string
  userId: string
  userEmail: string
  userRole?: UserRole
  action: AuditLog['action']
  resource: AuditLog['resource'] | string
  resourceId: string
  description?: string
  details?: Record<string, unknown>
  previousValue?: Record<string, unknown>
  newValue?: Record<string, unknown>
  changedFields?: string[]
  ipAddress?: string
  userAgent?: string
}

interface AuditLogFilters {
  userId?: string
  action?: AuditLog['action']
  resource?: AuditLog['resource']
  resourceId?: string
  startDate?: Date
  endDate?: Date
}

// ============================================
// CREATE AUDIT LOG
// ============================================

export async function createAuditLog(params: CreateAuditLogParams): Promise<string> {
  const {
    agencyId,
    userId,
    userEmail,
    userRole,
    action,
    resource,
    resourceId,
    description,
    details,
    previousValue,
    newValue,
    changedFields,
    ipAddress,
    userAgent,
  } = params

  const auditData = {
    agencyId,
    userId,
    userEmail,
    userRole: userRole || null,
    action,
    resource,
    resourceId,
    description: description || null,
    details: details || null,
    previousValue: previousValue || null,
    newValue: newValue || null,
    changedFields: changedFields || null,
    ipAddress: ipAddress || null,
    userAgent: userAgent || null,
    timestamp: serverTimestamp(),
  }

  const docRef = await addDoc(collection(db, 'auditLogs'), auditData)

  return docRef.id
}

// ============================================
// GET AUDIT LOGS
// ============================================

export async function getAuditLogs(
  agencyId: string,
  filters: AuditLogFilters = {},
  pageSize: number = 50,
  lastDoc?: unknown
): Promise<{ logs: AuditLog[]; lastDoc: unknown | null }> {
  const { userId, action, resource, resourceId, startDate, endDate } = filters

  // Build query constraints
  const constraints: unknown[] = [
    where('agencyId', '==', agencyId),
    orderBy('timestamp', 'desc'),
  ]

  if (userId) {
    constraints.push(where('userId', '==', userId))
  }

  if (action) {
    constraints.push(where('action', '==', action))
  }

  if (resource) {
    constraints.push(where('resource', '==', resource))
  }

  if (resourceId) {
    constraints.push(where('resourceId', '==', resourceId))
  }

  if (startDate) {
    constraints.push(where('timestamp', '>=', Timestamp.fromDate(startDate)))
  }

  if (endDate) {
    constraints.push(where('timestamp', '<=', Timestamp.fromDate(endDate)))
  }

  if (lastDoc) {
    constraints.push(startAfter(lastDoc))
  }

  constraints.push(limit(pageSize))

  const q = query(collection(db, 'auditLogs'), ...(constraints as never[]))
  const snapshot = await getDocs(q)

  const logs = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as AuditLog[]

  const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null

  return { logs, lastDoc: newLastDoc }
}

// ============================================
// GET AUDIT LOGS FOR RESOURCE
// ============================================

export async function getResourceAuditLogs(
  agencyId: string,
  resource: AuditLog['resource'],
  resourceId: string,
  pageSize: number = 20
): Promise<AuditLog[]> {
  const q = query(
    collection(db, 'auditLogs'),
    where('agencyId', '==', agencyId),
    where('resource', '==', resource),
    where('resourceId', '==', resourceId),
    orderBy('timestamp', 'desc'),
    limit(pageSize)
  )

  const snapshot = await getDocs(q)

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as AuditLog[]
}

// ============================================
// GET USER ACTIVITY
// ============================================

export async function getUserActivityLogs(
  agencyId: string,
  userId: string,
  pageSize: number = 50
): Promise<AuditLog[]> {
  const q = query(
    collection(db, 'auditLogs'),
    where('agencyId', '==', agencyId),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limit(pageSize)
  )

  const snapshot = await getDocs(q)

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as AuditLog[]
}

// ============================================
// AUDIT HELPERS
// ============================================

export function getChangedFields(
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>
): string[] {
  const changedFields: string[] = []

  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

  for (const key of allKeys) {
    if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
      changedFields.push(key)
    }
  }

  return changedFields
}

export function formatAuditAction(action: AuditLog['action']): string {
  const actionMap: Record<string, string> = {
    create: 'Created',
    update: 'Updated',
    delete: 'Deleted',
    archive: 'Archived',
    view: 'Viewed',
    export: 'Exported',
    login: 'Logged in',
    logout: 'Logged out',
  }

  return actionMap[action] || action || ''
}

export function formatAuditResource(resource: string): string {
  const resourceMap: Record<string, string> = {
    client: 'Client',
    user: 'User',
    visit: 'Visit',
    incident: 'Incident',
    authorization: 'Authorization',
    poc: 'Plan of Care',
    planOfCare: 'Plan of Care',
    carePlan: 'Care Plan',
    note: 'Note',
    document: 'Document',
    settings: 'Settings',
    notification: 'Notification',
    message: 'Message',
    caregiver: 'Caregiver',
    shiftSwap: 'Shift Swap',
    availability: 'Availability',
  }

  return resourceMap[resource] || resource || ''
}

// ============================================
// LOG SPECIFIC ACTIONS
// ============================================

export async function logClientView(params: {
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
  clientId: string
  clientName: string
}): Promise<void> {
  await createAuditLog({
    ...params,
    action: 'view',
    resource: 'client',
    resourceId: params.clientId,
    description: `Viewed client profile: ${params.clientName}`,
  })
}

export async function logUserLogin(params: {
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
  ipAddress?: string
  userAgent?: string
}): Promise<void> {
  await createAuditLog({
    ...params,
    action: 'login',
    resource: 'user',
    resourceId: params.userId,
    description: `User logged in: ${params.userEmail}`,
  })
}

export async function logUserLogout(params: {
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
}): Promise<void> {
  await createAuditLog({
    ...params,
    action: 'logout',
    resource: 'user',
    resourceId: params.userId,
    description: `User logged out: ${params.userEmail}`,
  })
}

export async function logDataExport(params: {
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
  resource: AuditLog['resource']
  exportType: string
  recordCount: number
}): Promise<void> {
  await createAuditLog({
    ...params,
    action: 'export',
    resourceId: 'bulk',
    description: `Exported ${params.recordCount} ${params.resource} records as ${params.exportType}`,
  })
}
